import { DEFAULT_LANGUAGE, LANG_PREF_COOKIE_NAME, SPANISH_LANG_CODE, SUPPORTED_LANGUAGES } from "utilities/Constant";
import {
  getPreferredLanguage,
  isSpanishLanguage,
  hasExplicitUserLanguageSelection,
  normalizeToAppLanguage,
  resolveLanguageForMyLoansLanding,
  setPersistedPreferredLanguage,
  shouldDeferDealTranslationUntilAck,
} from "utilities/getPreferredLanguage";

const POST_AUTH_PATH = "/post-auth";
const MY_LOANS_PATH = "/my-loans";

const setLangPrefCookie = (langCode: string): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=${langCode};path=/`;
};

export const clearLangPrefCookie = (): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const getLangPrefCookie = (): string | null => {
  const rawCookie = document.cookie;
  if (!rawCookie?.trim()) {
    return null;
  }

  const prefix = `${LANG_PREF_COOKIE_NAME}=`;
  const cookie = rawCookie
    .split(";")
    .map((part) => part?.trim())
    .find((part) => part?.startsWith(prefix));

  if (!cookie) {
    return null;
  }

  const value = cookie.slice(prefix.length).trim();
  return value || null;
};

export const syncLangPrefCookie = (language: string): void => {
  if (isSpanishLanguage(language)) {
    setLangPrefCookie(SPANISH_LANG_CODE);
    return;
  }

  clearLangPrefCookie();
};

export const getCookiePreferredLanguage = (): string => {
  const cookieValue = getLangPrefCookie();
  if (cookieValue === SPANISH_LANG_CODE || isSpanishLanguage(cookieValue)) {
    return SUPPORTED_LANGUAGES[1];
  }

  return DEFAULT_LANGUAGE;
};

export const isLangPrefCookieMismatched = (nextLanguage: string): boolean => {
  return getCookiePreferredLanguage() !== normalizeToAppLanguage(nextLanguage);
};

export const isRegistrationTranslationDeferPath = (pathname?: string | null): boolean => {
  const path = (pathname ?? "").split("?")[0].toLowerCase();
  return (
    path.startsWith("/registration") ||
    path.startsWith("/form")
  );
};

export const resolveUnauthenticatedDisplayLanguage = (
  reduxLanguage?: string | null,
  pathname?: string | null,
): string => {
  if (isRegistrationTranslationDeferPath(pathname)) {
    if (!hasExplicitUserLanguageSelection()) {
      return DEFAULT_LANGUAGE;
    }

    return getPreferredLanguage(reduxLanguage);
  }

  const cookieLanguage = getCookiePreferredLanguage();
  if (isSpanishLanguage(cookieLanguage)) {
    return cookieLanguage;
  }

  return getPreferredLanguage(reduxLanguage);
};

export type EnterMyLoansWithLangPrefOptions = {
  readonly language: string;
  readonly isPreferredLanguageEnabled: boolean;
  readonly spaNavigate: () => void;
  readonly persistFlush?: () => Promise<unknown>;
};

export const enterMyLoansWithLangPref = async ({
  language,
  isPreferredLanguageEnabled,
  spaNavigate,
  persistFlush,
}: EnterMyLoansWithLangPrefOptions): Promise<boolean> => {
  if (!isPreferredLanguageEnabled || !isLangPrefCookieMismatched(language)) {
    spaNavigate();
    return false;
  }

  syncLangPrefCookie(language);
  if (persistFlush) {
    await persistFlush();
  }
  globalThis.location.replace(MY_LOANS_PATH);
  return true;
};

export type EnterMyLoansAfterAuthOptions = {
  readonly userId: string;
  readonly reduxLanguage?: string | null;
  readonly isPreferredLanguageEnabled: boolean;
  readonly fetchUserDbLanguage: (userId: string) => Promise<string | null | undefined>;
  readonly persistFlush: () => Promise<unknown>;
  readonly spaNavigate: () => void;
};

export const enterMyLoansAfterAuth = async ({
  userId,
  reduxLanguage,
  isPreferredLanguageEnabled,
  fetchUserDbLanguage,
  persistFlush,
  spaNavigate,
}: EnterMyLoansAfterAuthOptions): Promise<void> => {
  if (!isPreferredLanguageEnabled || !userId?.trim()) {
    spaNavigate();
    return;
  }

  let userDbLanguage: string | null | undefined;
  try {
    userDbLanguage = await fetchUserDbLanguage(userId);
  } catch {
    spaNavigate();
    return;
  }

  const language = resolveLanguageForMyLoansLanding(userDbLanguage, reduxLanguage);
  await enterMyLoansWithLangPref({
    language,
    isPreferredLanguageEnabled: true,
    spaNavigate,
    persistFlush,
  });
};

export type EnterPostAuthWithLangPrefOptions = {
  readonly language: string;
  readonly isPreferredLanguageEnabled: boolean;
  readonly spaNavigate: () => void;
  readonly persistFlush: () => Promise<unknown>;
  readonly showAcknowledgementModal?: boolean;
  readonly isPostLos?: boolean;
};

export const enterPostAuthWithLangPref = async ({
  language,
  isPreferredLanguageEnabled,
  spaNavigate,
  persistFlush,
  showAcknowledgementModal,
  isPostLos,
}: EnterPostAuthWithLangPrefOptions): Promise<void> => {
  if (!isPreferredLanguageEnabled) {
    spaNavigate();
    return;
  }

  if (
    !hasExplicitUserLanguageSelection() &&
    shouldDeferDealTranslationUntilAck({
      showAcknowledgementModal,
      isPostLos,
    })
  ) {
    spaNavigate();
    return;
  }

  if (!isLangPrefCookieMismatched(language)) {
    spaNavigate();
    return;
  }

  syncLangPrefCookie(language);
  if (!hasExplicitUserLanguageSelection()) {
    setPersistedPreferredLanguage(language);
  }
  await persistFlush();
  globalThis.location.replace(POST_AUTH_PATH);
};
